type ChartUser = { id: number; managerId?: number };

/**
 * Returns the manager id to draw for each user. The chart starts from users
 * without a manager, so a user is put on top when their manager is not loaded,
 * and a manager loop (A manages B, B manages A) is broken at the person in the
 * loop with the most direct reports. Otherwise the loop would hide everyone.
 */
export const getChartManagerIds = (
  users: ChartUser[],
): Record<number, number | undefined> => {
  const managerIds: Record<number, number | undefined> = {};
  users.forEach((user) => {
    managerIds[user.id] = user.managerId;
  });
  users.forEach((user) => {
    const managerId = managerIds[user.id];
    if (managerId !== undefined && !(managerId in managerIds)) {
      managerIds[user.id] = undefined;
    }
  });

  const reportCounts: Record<number, number> = {};
  Object.values(managerIds).forEach((managerId) => {
    if (managerId !== undefined) {
      reportCounts[managerId] = (reportCounts[managerId] || 0) + 1;
    }
  });

  const checkedIds = new Set<number>();
  users.forEach((user) => {
    const path: number[] = [];
    let currentId: number | undefined = user.id;
    while (currentId !== undefined && !checkedIds.has(currentId)) {
      const loopStart = path.indexOf(currentId);
      if (loopStart !== -1) {
        const topId = path
          .slice(loopStart)
          .reduce((best, id) =>
            (reportCounts[id] || 0) > (reportCounts[best] || 0) ? id : best,
          );
        managerIds[topId] = undefined;
        break;
      }
      path.push(currentId);
      currentId = managerIds[currentId];
    }
    path.forEach((id) => checkedIds.add(id));
  });

  return managerIds;
};
